import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react'
import type { CandleData } from '../components/GraphCandle'
import { botApi } from '../api'
import { getCandleCountForTimeframe } from '../../utils/chartUtils'
import { toApiTimestamp } from '../utils/timeParsing'

const SAVE_INTERVAL_MS = 5000

function getPlayTiming(speed: number): { intervalMs: number; batchSize: number } {
  if (speed >= 50) return { intervalMs: 100, batchSize: 10 }
  if (speed >= 25) return { intervalMs: 100, batchSize: 5 }
  if (speed >= 10) return { intervalMs: 100, batchSize: 2 }
  if (speed >= 5) return { intervalMs: 100, batchSize: 1 }
  if (speed >= 2) return { intervalMs: 250, batchSize: 1 }
  if (speed >= 1) return { intervalMs: 500, batchSize: 1 }
  return { intervalMs: 1000, batchSize: 1 }
}

function binarySearchFirstAfter(candles: CandleData[], ts: number): number {
  let lo = 0
  let hi = candles.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (candles[mid].time <= ts) lo = mid + 1
    else hi = mid
  }
  return lo
}

export interface UsePlaybackOptions {
  btid: string
  rangeStartTs: number
  rangeEndTs: number
  progressViewTs: number
  onProgressViewChange: (ts: number) => void
  timeframe: string
  speed: number
  candlesRef: React.RefObject<CandleData[]>
  maxAvailableTsRef: React.RefObject<number | null>
  extendWindow: (direction: 'left' | 'right') => Promise<void>
  loadWindow: (centerDate: Date) => Promise<void>
  applyVisibleRange: (range: { from: number; to: number }) => void
}

export interface UsePlaybackReturn {
  isPlaying: boolean
  play: () => void
  pause: () => void
  reset: () => void
  jumpTo: (ts: number) => void
  saveNow: () => void
  playStartedAtMs: number | null
  playElapsedSec: number
}

export default function usePlayback({
  btid,
  rangeStartTs,
  rangeEndTs,
  progressViewTs,
  onProgressViewChange,
  timeframe,
  speed,
  candlesRef,
  maxAvailableTsRef,
  extendWindow,
  loadWindow,
  applyVisibleRange,
}: UsePlaybackOptions): UsePlaybackReturn {
  const [isPlaying, setIsPlaying] = useState(false)
  const [playStartedAtMs, setPlayStartedAtMs] = useState<number | null>(null)
  const [playElapsedSec, setPlayElapsedSec] = useState(0)

  const isPlayingRef = useRef(false)
  const progressViewTsRef = useRef(progressViewTs)
  const lastSavedTsRef = useRef(progressViewTs)
  const speedRef = useRef(speed)
  const timeframeRef = useRef(timeframe)
  const playStartedAtMsRef = useRef<number | null>(null)
  // Pending debounced save (from rapid jumpTo calls). Cleared on commit/unmount.
  const pendingSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingSaveTsRef = useRef<number | null>(null)

  useLayoutEffect(() => {
    playStartedAtMsRef.current = playStartedAtMs
  }, [playStartedAtMs])

  // Accumulate the time spent in the current play session into playElapsedSec,
  // then clear the start marker. Safe to call when not currently playing (no-op).
  const accumulatePlayTime = useCallback(() => {
    const startedAt = playStartedAtMsRef.current
    if (startedAt == null) return
    const delta = (Date.now() - startedAt) / 1000
    playStartedAtMsRef.current = null
    setPlayStartedAtMs(null)
    if (delta > 0) {
      setPlayElapsedSec(prev => prev + delta)
    }
  }, [])

  useLayoutEffect(() => { isPlayingRef.current = isPlaying }, [isPlaying])
  useLayoutEffect(() => { progressViewTsRef.current = progressViewTs }, [progressViewTs])
  useLayoutEffect(() => { speedRef.current = speed }, [speed])
  useLayoutEffect(() => { timeframeRef.current = timeframe }, [timeframe])

  const saveToDB = useCallback((ts: number) => {
    if (ts === lastSavedTsRef.current) return
    // Cancel any pending debounced save — we're committing immediately.
    if (pendingSaveTimerRef.current) {
      clearTimeout(pendingSaveTimerRef.current)
      pendingSaveTimerRef.current = null
      pendingSaveTsRef.current = null
    }
    lastSavedTsRef.current = ts
    botApi.updateProgressView(btid, toApiTimestamp(ts)).catch(err => {
      if (import.meta.env.DEV) console.error('Failed to save progress_view:', err)
    })
  }, [btid])

  /**
   * Debounced save for rapid scrub interactions. Coalesces N quick clicks
   * into a single API call once the user pauses for SCRUB_SAVE_DEBOUNCE_MS.
   * The 5s periodic interval + unmount handler still flush any pending state
   * so we never lose progress.
   */
  const SCRUB_SAVE_DEBOUNCE_MS = 300
  const scheduleScrubSave = useCallback((ts: number) => {
    pendingSaveTsRef.current = ts
    if (pendingSaveTimerRef.current) clearTimeout(pendingSaveTimerRef.current)
    pendingSaveTimerRef.current = setTimeout(() => {
      pendingSaveTimerRef.current = null
      const pending = pendingSaveTsRef.current
      pendingSaveTsRef.current = null
      if (pending !== null) saveToDB(pending)
    }, SCRUB_SAVE_DEBOUNCE_MS)
  }, [saveToDB])

  // Periodic DB save every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      const current = progressViewTsRef.current
      if (current !== lastSavedTsRef.current) {
        saveToDB(current)
      }
    }, SAVE_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [saveToDB])

  // Save on unmount — also flush any pending debounced scrub save so the
  // last position the user dragged to is persisted before teardown.
  useEffect(() => {
    return () => {
      if (pendingSaveTimerRef.current) {
        clearTimeout(pendingSaveTimerRef.current)
        pendingSaveTimerRef.current = null
      }
      const current = progressViewTsRef.current
      if (current !== lastSavedTsRef.current) {
        saveToDB(current)
      }
      pendingSaveTsRef.current = null
    }
  }, [saveToDB])

  // ---- Play loop ----
  useEffect(() => {
    if (!isPlaying) return

    let cancelled = false
    let timerId: ReturnType<typeof setTimeout>
    let extensionInFlight = false

    const tick = () => {
      if (cancelled) return

      const candles = candlesRef.current ?? []
      const currentTs = progressViewTsRef.current
      const maxTs = maxAvailableTsRef.current
      const { batchSize } = getPlayTiming(speedRef.current)

      const nextIdx = binarySearchFirstAfter(candles, currentTs)

      if (nextIdx >= candles.length) {
        if (!extensionInFlight) {
          extensionInFlight = true
          extendWindow('right').finally(() => { extensionInFlight = false })
        }
        timerId = setTimeout(tick, 200)
        return
      }

      // Cap the batch at the last buffered candle so we don't skip candles
      // that ARE available just because the batch endpoint runs past maxTs.
      let targetIdx = Math.min(nextIdx + batchSize - 1, candles.length - 1)
      if (maxTs !== null) {
        // Find the largest index whose candle.time <= maxTs.
        let lo = 0
        let hi = candles.length
        while (lo < hi) {
          const mid = (lo + hi) >> 1
          if (candles[mid].time <= maxTs) lo = mid + 1
          else hi = mid
        }
        const lastBufferedIdx = lo - 1
        if (lastBufferedIdx < nextIdx) {
          // Nothing buffered yet at/after currentTs; wait and retry.
          timerId = setTimeout(tick, 500)
          return
        }
        targetIdx = Math.min(targetIdx, lastBufferedIdx)
      }

      const targetTs = candles[targetIdx].time

      if (targetTs > rangeEndTs) {
        accumulatePlayTime()
        setIsPlaying(false)
        return
      }

      onProgressViewChange(targetTs)

      const displayCount = targetIdx + 1
      const vc = getCandleCountForTimeframe(timeframeRef.current, displayCount)
      const lastVisibleIdx = displayCount - 1
      const from = Math.max(0, lastVisibleIdx - vc + 1)
      applyVisibleRange({ from, to: lastVisibleIdx })

      if (!extensionInFlight && candles.length > 0 && targetIdx > candles.length * 0.8) {
        extensionInFlight = true
        extendWindow('right').finally(() => { extensionInFlight = false })
      }

      const { intervalMs } = getPlayTiming(speedRef.current)
      timerId = setTimeout(tick, intervalMs)
    }

    const { intervalMs } = getPlayTiming(speed)
    timerId = setTimeout(tick, intervalMs)

    return () => {
      cancelled = true
      clearTimeout(timerId)
    }
  }, [isPlaying, speed, timeframe, candlesRef, maxAvailableTsRef, extendWindow, rangeEndTs, onProgressViewChange, applyVisibleRange, accumulatePlayTime])

  const play = useCallback(() => {
    // Idempotent: if we're already playing, don't reset playStartedAtMs (that
    // would silently zero out the elapsed counter in the status pill).
    if (isPlayingRef.current) return

    setIsPlaying(true)
    const now = Date.now()
    playStartedAtMsRef.current = now
    setPlayStartedAtMs(now)

    const currentTs = progressViewTsRef.current
    const candles = candlesRef.current ?? []
    const isCurrentWindowLoaded =
      candles.length > 0 &&
      currentTs >= candles[0].time &&
      currentTs <= candles[candles.length - 1].time

    if (!isCurrentWindowLoaded) {
      loadWindow(new Date(currentTs * 1000))
    }
  }, [candlesRef, loadWindow])

  const pause = useCallback(() => {
    accumulatePlayTime()
    setIsPlaying(false)
    const current = progressViewTsRef.current
    if (current !== lastSavedTsRef.current) {
      saveToDB(current)
    }
  }, [saveToDB, accumulatePlayTime])

  const reset = useCallback(() => {
    accumulatePlayTime()
    setPlayElapsedSec(0)
    setIsPlaying(false)
    onProgressViewChange(rangeStartTs)
    saveToDB(rangeStartTs)
    loadWindow(new Date(rangeStartTs * 1000))
  }, [rangeStartTs, saveToDB, loadWindow, onProgressViewChange, accumulatePlayTime])

  // Any scrub (forward or backward) pauses the replay. This eliminates a race
  // where the play loop ticks against stale candles while loadWindow is in
  // flight for the new center. saveToDB is debounced so a rapid drag doesn't
  // hammer the API; the 5s periodic interval still backstops it.
  const jumpTo = useCallback((ts: number) => {
    accumulatePlayTime()
    setIsPlaying(false)
    onProgressViewChange(ts)
    scheduleScrubSave(ts)
    loadWindow(new Date(ts * 1000))
  }, [scheduleScrubSave, loadWindow, onProgressViewChange, accumulatePlayTime])

  const saveNow = useCallback(() => {
    saveToDB(progressViewTsRef.current)
  }, [saveToDB])

  return {
    isPlaying,
    play,
    pause,
    reset,
    jumpTo,
    saveNow,
    playStartedAtMs,
    playElapsedSec,
  }
}
